/** AES-256-GCM decryption matching secret_manager.py on the agent app. */

export interface PatientPii {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  birthday?: string;
  email?: string;
  phone1?: string;
}

async function importKey(hexSecret: string): Promise<CryptoKey> {
  const bytes = new Uint8Array(hexSecret.match(/.{2}/g)!.map(h => parseInt(h, 16)));
  return crypto.subtle.importKey('raw', bytes, 'AES-GCM', false, ['decrypt']);
}

export async function decryptPii(hexSecret: string, b64Ciphertext: string): Promise<PatientPii> {
  const raw = Uint8Array.from(atob(b64Ciphertext), c => c.charCodeAt(0));
  const iv = raw.slice(0, 12);
  const data = raw.slice(12);
  const key = await importKey(hexSecret);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
  return JSON.parse(new TextDecoder().decode(decrypted)) as PatientPii;
}

export async function decryptPiiBatch(
  hexSecret: string,
  entries: Array<{ patient_uuid: string; encrypted_pii: string | null }>,
): Promise<Record<string, PatientPii>> {
  const key = await importKey(hexSecret);
  const result: Record<string, PatientPii> = {};
  await Promise.all(
    entries.map(async ({ patient_uuid, encrypted_pii }) => {
      if (!encrypted_pii) return;
      try {
        const raw = Uint8Array.from(atob(encrypted_pii), c => c.charCodeAt(0));
        const iv = raw.slice(0, 12);
        const data = raw.slice(12);
        const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
        result[patient_uuid] = JSON.parse(new TextDecoder().decode(dec));
      } catch {
        // wrong key or corrupt ciphertext — skip silently
      }
    }),
  );
  return result;
}
